<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useMeta } from '@/stores/meta.js'
import { formatDateTime } from '@/lib/format.js'
import RecordLink from '@/components/record/RecordLink.vue'

/**
 * An activity planned again after another (D-230): "2nd attempt", and on hover — or, `full`, in
 * a line with a link — when the one before was and how it ended.
 */
const props = defineProps({ activity: { type: Object, required: true }, full: Boolean })
const { t } = useI18n()
const meta = useMeta()
const f = computed(() => props.activity.follow_up)
const before = computed(() => {
  const p = f.value?.previous
  if (!p) return ''
  const outcome = p.outcome ? meta.dictionaryLabel('activity_outcome', p.outcome) : null
  return [p.completed_at ? formatDateTime(p.completed_at) : null, outcome].filter(Boolean).join(' — ')
})
</script>

<template>
  <template v-if="f">
    <p v-if="full" class="flex flex-wrap items-center gap-x-1.5 text-sm text-amber-700 dark:text-amber-300" data-testid="follow-up-line">
      <i class="fa-solid fa-rotate-right" aria-hidden="true"></i>
      {{ t('activity.followUp.again', { n: f.attempt }) }}<template v-if="before"> · {{ t('activity.followUp.before') }}
        <RecordLink class="link" entity="activity" :uuid="f.previous.uuid">{{ before }}</RecordLink></template>
    </p>
    <span v-else class="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-amber-100 px-1.5 py-0.5 text-2xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200" :title="before ? `${t('activity.followUp.before')} ${before}` : ''" data-testid="follow-up-badge">
      <i class="fa-solid fa-rotate-right text-3xs" aria-hidden="true"></i>{{ t('activity.followUp.badge', { n: f.attempt }) }}
    </span>
  </template>
</template>
